import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Logo } from "./Logo";

export function LangSwitch({ id = "header", dark = false }) {
  const { lang, switchPath } = useLang();
  const base = "px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors";
  return (
    <div className={`inline-flex items-center rounded-full border p-0.5 ${dark ? "border-white/20" : "border-slate-300"}`} data-testid={`${id}-language-toggle`} role="group" aria-label="Langue / Language">
      {["fr", "en"].map((l) => (
        l === lang ? (
          <span key={l} className={`${base} ${dark ? "bg-white text-slate-900" : "bg-slate-900 text-white"}`} data-testid={`${id}-lang-${l}`} aria-current="true">{l}</span>
        ) : (
          <Link key={l} to={switchPath} className={`${base} ${dark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid={`${id}-lang-${l}`}>{l}</Link>
        )
      ))}
    </div>
  );
}

export function Header() {
  const { t, to } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "home", label: t.nav.home, end: true },
    { key: "services", label: t.nav.services },
    { key: "about", label: t.nav.about },
    { key: "contact", label: t.nav.contact },
  ];

  const linkCls = ({ isActive }) => `relative px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-teal-700" : "text-slate-700 hover:text-slate-900"}`;

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${scrolled || open ? "border-b border-slate-200/70 bg-white/85 shadow-[0_8px_30px_-20px_rgba(15,23,42,0.3)] backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`} data-testid="site-header">
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link to={to("home")} aria-label="SIDLY – Accueil" data-testid="header-logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigation principale">
          {links.map((l) => (
            <NavLink key={l.key} to={to(l.key)} end={l.end} className={linkCls} data-testid={`nav-${l.key}-link`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <LangSwitch />
          <Link to={to("contact")} className="btn-primary !py-2.5" data-testid="header-quote-button">
            {t.nav.quote} <ArrowUpRight size={16} />
          </Link>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-900 lg:hidden" aria-label={open ? t.nav.close : t.nav.menu} aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-menu">
            <div className="container-x flex flex-col gap-1 py-5">
              {links.map((l, i) => (
                <motion.div key={l.key} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={to(l.key)} end={l.end} onClick={() => setOpen(false)} className={({ isActive }) => `block rounded-2xl px-4 py-3 font-display text-lg font-semibold ${isActive ? "bg-teal-50 text-teal-700" : "text-slate-900"}`} data-testid={`mobile-nav-${l.key}-link`}>{l.label}</NavLink>
                </motion.div>
              ))}
              <div className="mt-4 flex items-center justify-between gap-4 px-4">
                <LangSwitch id="mobile" />
                <Link to={to("contact")} onClick={() => setOpen(false)} className="btn-primary !py-2.5" data-testid="mobile-quote-button">{t.nav.quote}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
